import { useState } from "react";
import { Store, ChevronDown, Check, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

/**
 * Store Selector Component
 * Allows users with multiple stores (Operator/Agency plans) to switch between stores.
 * 
 * Props:
 * - stores: Array of store objects [{store_id, shop_domain, connected_at, ...}]
 * - activeStoreId: Currently selected store ID
 * - onStoreChange: Callback when store selection changes
 * - storeLimit: Max stores allowed on current plan
 * - onAddStore: Callback to add a new store (optional)
 * - disabled: Disable the selector
 */
const StoreSelector = ({ 
  stores = [], 
  activeStoreId, 
  onStoreChange, 
  storeLimit = 1,
  onAddStore,
  disabled = false 
}) => {
  const [open, setOpen] = useState(false);
  
  // Find the active store
  const activeStore = stores.find(s => s.store_id === activeStoreId);
  const canAddStore = stores.length < storeLimit;
  
  // If only one store or no stores, don't show selector
  if (stores.length <= 1 && !canAddStore) {
    return null;
  }
  
  const handleStoreSelect = (store) => {
    if (store.store_id !== activeStoreId) {
      onStoreChange(store.store_id);
    }
    setOpen(false);
  };
  
  // Format store name for display
  const formatStoreName = (domain) => {
    if (!domain) return "Unknown Store";
    // Remove .myshopify.com suffix for cleaner display
    return domain.replace(".myshopify.com", "");
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="bg-surface border-[#262626] hover:bg-[#1E1E1E] text-white min-w-[200px] justify-between"
          disabled={disabled}
          data-testid="store-selector"
        >
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-[#D4F34A]" />
            <span className="truncate max-w-[150px]">
              {activeStore ? formatStoreName(activeStore.shop_domain) : "Select Store"}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-text-muted ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="bg-surface border-[#262626] min-w-[220px]"
        align="start"
      >
        {stores.map((store) => (
          <DropdownMenuItem
            key={store.store_id}
            className="flex items-center justify-between cursor-pointer hover:bg-[#1E1E1E] focus:bg-[#1E1E1E]"
            onClick={() => handleStoreSelect(store)}
            data-testid={`store-option-${store.store_id}`}
          >
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-text-muted" />
              <span className="truncate max-w-[150px]">
                {formatStoreName(store.shop_domain)}
              </span>
            </div>
            {store.store_id === activeStoreId && (
              <Check className="w-4 h-4 text-[#D4F34A]" />
            )}
          </DropdownMenuItem>
        ))}
        
        {canAddStore && onAddStore && (
          <>
            <DropdownMenuSeparator className="bg-[#262626]" />
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer hover:bg-[#1E1E1E] focus:bg-[#1E1E1E] text-[#D4F34A]"
              onClick={() => {
                setOpen(false);
                onAddStore();
              }}
              data-testid="add-store-option"
            >
              <Plus className="w-4 h-4" />
              <span>Add Another Store</span>
              <span className="text-xs text-text-muted ml-auto">
                {stores.length}/{storeLimit}
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StoreSelector;
